import React, { useEffect, useState } from "react"
import { MDBDataTable } from "mdbreact"
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
} from "reactstrap"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import { teamsAPI, usersAPI } from "../../services/api"
import AlertModal from "../../components/Common/AlertModal"

const TeamList = (props) => {
    document.title = "Gestion des Équipes | Ticket Management"

    const [teams, setTeams] = useState([])
    const [users, setUsers] = useState([])
    const [modal, setModal] = useState(false)
    const [membersModal, setMembersModal] = useState(false)
    const [editingTeam, setEditingTeam] = useState(null)
    const [selectedTeam, setSelectedTeam] = useState(null)

    const [formData, setFormData] = useState({
        nom: "",
        description: "",
    })

    const [alertConfig, setAlertConfig] = useState({
        isOpen: false,
        type: 'success',
        message: '',
        title: ''
    })

    const toggleAlert = () => setAlertConfig({ ...alertConfig, isOpen: !alertConfig.isOpen })

    const breadcrumbItems = [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Équipes", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Gestion des Équipes", breadcrumbItems)
        fetchData()
        fetchUsers()
    }, [])

    const fetchData = async () => {
        try {
            const res = await teamsAPI.getAll()
            setTeams(res.data)
        } catch (error) {
            console.error("Erreur lors du chargement des équipes", error)
        }
    }

    const fetchUsers = async () => {
        try {
            const res = await usersAPI.getAll()
            // Inclure tous les utilisateurs, y compris les clients
            setUsers(res.data)
        } catch (error) {
            console.error("Erreur lors du chargement des utilisateurs", error)
        }
    }

    const toggleModal = () => {
        setModal(!modal)
        if (!modal) {
            setEditingTeam(null)
            setFormData({
                nom: "",
                description: "",
            })
        }
    }

    const toggleMembersModal = (team = null) => {
        setSelectedTeam(team)
        setMembersModal(!membersModal)
    }

    const handleEdit = (team) => {
        setEditingTeam(team)
        setFormData({
            nom: team.nom,
            description: team.description || "",
        })
        setModal(true)
    }

    const handleDelete = async (id) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer cette équipe ?")) {
            try {
                await teamsAPI.delete(id)
                fetchData()
            } catch (error) {
                console.error("Erreur lors de la suppression", error)
            }
        }
    }

    const handleMemberToggle = async (user, isMember) => {
        try {
            await teamsAPI.toggleMember(selectedTeam.id, user.id);

            // Rafraîchir localement pour une UI réactive
            fetchData();
            fetchUsers();

            // Mettre à jour selectedTeam localement pour voir les changements dans le modal
            if (isMember) {
                setSelectedTeam({
                    ...selectedTeam,
                    members: selectedTeam.members.filter(m => m.id !== user.id)
                });
            } else {
                setSelectedTeam({
                    ...selectedTeam,
                    members: [...(selectedTeam.members || []), user]
                });
            }
        } catch (error) {
            console.error("Erreur lors de la mise à jour du membre", error);
        }
    }

    const data = {
        columns: [
            { label: "Nom équipe", field: "nom", sort: "asc" },
            { label: "Description", field: "description", sort: "asc" },
            { label: "Membres", field: "membersCount", sort: "asc" },
            { label: "Projets", field: "projetsCount", sort: "asc" },
            { label: "Actions", field: "actions", sort: "disabled" },
        ],
        rows: teams.map((t) => ({
            nom: t.nom,
            description: t.description || "-",
            membersCount: (t.members || []).length,
            projetsCount: (t.projets || []).length,
            actions: (
                <div className="d-flex gap-2">
                    <Button color="info" size="sm" onClick={() => toggleMembersModal(t)} title="Gérer les membres">
                        <i className="mdi mdi-account-group"></i>
                    </Button>
                    <Button color="primary" size="sm" onClick={() => handleEdit(t)}>
                        <i className="mdi mdi-pencil"></i>
                    </Button>
                    <Button color="danger" size="sm" onClick={() => handleDelete(t.id)}>
                        <i className="mdi mdi-trash-can"></i>
                    </Button>
                </div>
            ),
        })),
    }

    return (
        <React.Fragment>
            <Row>
                <Col className="col-12">
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <CardTitle className="h4">Liste des Équipes</CardTitle>
                                <Button color="success" onClick={toggleModal}>
                                    <i className="mdi mdi-plus me-1"></i> Nouvelle Équipe
                                </Button>
                            </div>

                            <MDBDataTable
                                noBottomColumns
                                responsive
                                striped
                                bordered
                                data={data}
                                entriesLabel="Afficher"
                                searchLabel="Rechercher"
                                infoLabel={["Affichage de", "à", "sur", "entrées"]}
                                paginationLabel={["Précédent", "Suivant"]}
                                noRecordsFoundLabel="Aucun enregistrement trouvé"
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Modal isOpen={modal} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>
                    {editingTeam ? "Modifier l'Équipe" : "Nouvelle Équipe"}
                </ModalHeader>
                <Form onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                        if (editingTeam) {
                            await teamsAPI.update(editingTeam.id, formData)
                            setAlertConfig({
                                isOpen: true,
                                type: 'success',
                                title: 'Mise à jour',
                                message: "L'équipe a été mise à jour avec succès."
                            })
                        } else {
                            await teamsAPI.create(formData)
                            setAlertConfig({
                                isOpen: true,
                                type: 'success',
                                title: 'Créée',
                                message: "L'équipe a été créée avec succès."
                            })
                        }
                        toggleModal()
                        fetchData()
                    } catch (error) {
                        console.error("Erreur enregistrement équipe", error)
                        setAlertConfig({
                            isOpen: true,
                            type: 'error',
                            title: 'Erreur',
                            message: "Une erreur est survenue lors de l'enregistrement de l'équipe."
                        })
                    }
                }}>
                    <ModalBody>
                        <FormGroup>
                            <Label for="nom">Nom de l'équipe</Label>
                            <Input
                                type="text"
                                id="nom"
                                value={formData.nom}
                                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="description">Description</Label>
                            <Input
                                type="textarea"
                                id="description"
                                rows="3"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggleModal}>Annuler</Button>
                        <Button color="primary" type="submit">Enregistrer</Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Modal de gestion des membres */}
            <Modal isOpen={membersModal} toggle={() => toggleMembersModal()} size="md">
                <ModalHeader toggle={() => toggleMembersModal()}>
                    Membres de l'équipe : {selectedTeam?.nom}
                </ModalHeader>
                <ModalBody>
                    <p className="text-muted mb-3">Sélectionnez les membres (collaborateurs ou clients) à assigner à cette équipe.</p>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <ul className="list-group list-group-flush">
                            {users.map(u => {
                                const isMember = selectedTeam?.members?.some(m => m.id === u.id);
                                return (
                                    <li key={u.id} className="list-group-item d-flex justify-content-between align-items-center px-0">
                                        <div>
                                            <div className="fw-bold">{u.name}</div>
                                            <small className="text-muted">{u.role} - {u.email}</small>
                                            {u.teams?.length > 0 && u.teams.some(team => team.id !== selectedTeam?.id) && (
                                                <div className="text-warning small">
                                                    <i className="mdi mdi-alert-circle-outline me-1"></i>
                                                    Également dans : {u.teams.filter(t => t.id !== selectedTeam?.id).map(t => t.nom).join(', ')}
                                                </div>
                                            )}
                                        </div>
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={isMember}
                                                onChange={() => handleMemberToggle(u, isMember)}
                                            />
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => toggleMembersModal()}>Fermer</Button>
                </ModalFooter>
            </Modal>

            <AlertModal
                isOpen={alertConfig.isOpen}
                toggle={toggleAlert}
                type={alertConfig.type}
                title={alertConfig.title}
                message={alertConfig.message}
            />
        </React.Fragment>
    )
}

export default connect(null, { setBreadcrumbItems })(TeamList)
